import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Navigate } from 'react-router-dom';
import { Spin, Alert } from 'antd';
import { InteractionStatus } from '@azure/msal-browser';
import { authService } from '../../services/authService';

const RedirectHandler: React.FC = () => {
  const { instance, inProgress, accounts } = useMsal();
  const [error, setError] = useState<string | null>(null);
  const [isHandled, setIsHandled] = useState(false);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        if (inProgress === InteractionStatus.HandleRedirect) {
          console.log('Waiting for redirect to be handled...');
          return;
        }

        // If we have the redirect response but no accounts, handle it
        if (inProgress === InteractionStatus.None && accounts.length === 0) {
          console.log('Attempting to handle redirect response...');
          const response = await instance.handleRedirectPromise();
          
          if (response) {
            console.log('Redirect handled successfully:', response.account?.username);

            // Debug the access token after successful authentication
            if (response.accessToken) {
              console.log('🎯 Debugging token from redirect response...');
              authService.debugAnyToken(response.accessToken);
            }

            // Also debug the ID token if available
            if (response.idToken) {
              console.log('🆔 Debugging ID token from redirect response...');
              authService.debugAnyToken(response.idToken);
            }
          } else {
            console.log('No redirect response found');
          }
        }

        // Mark as handled once processing is complete
        if (inProgress === InteractionStatus.None) {
          setIsHandled(true);
        }
      } catch (err) {
        console.error('Error handling redirect:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
        setIsHandled(true);
      }
    };

    handleRedirect();
  }, [instance, inProgress, accounts.length]);

  // Show loading while processing
  if (inProgress === InteractionStatus.HandleRedirect || !isHandled) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        gap: '20px'
      }}>
        <Spin size="large" tip="Processing authentication..." />
        <p style={{ color: '#666', fontSize: '14px' }}>
          Please wait while we complete your sign-in...
        </p>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px'
      }}>
        <Alert
          message="Authentication Error"
          description={error}
          type="error"
          showIcon
          action={
            <a href="/">Return to Home</a>
          }
          style={{ maxWidth: '500px' }}
        />
      </div>
    );
  }

  // If we have accounts, redirect to appropriate page
  if (accounts.length > 0) {
    // Check if user came from admin area by looking at redirectStartPage in sessionStorage
    const redirectStartPage = sessionStorage.getItem('msal.redirectStartPage') || '/admin/content-list';

    // If the original page was an admin route, redirect there, otherwise go to main
    const targetPage = redirectStartPage.startsWith('/admin') ? redirectStartPage : '/main';

    console.log('Authentication successful, redirecting to:', targetPage);

    // Clean up the stored redirect page
    sessionStorage.removeItem('msal.redirectStartPage');

    return <Navigate to={targetPage} replace />;
  }

  // If no accounts and no error, redirect to home
  console.log('No accounts found, redirecting to home');
  return <Navigate to="/" replace />;
};

export default RedirectHandler;